import {
  useLazyRehabForms,
  useLazyTransitionalCare,
} from "apollo/hooks/queries";
import { RECARE_FILE_TYPE } from "core/consts";
import { useIntParams } from "core/model/utils/urls";
import {
  AllPatientForms,
  FileForm,
  RehabForm,
  TransitionalCare,
} from "core/types";
import { useCallback } from "react";
import { convertEncryptedFieldIn } from "react-forms-state";
import { useTranslations } from "translations";
import { useCreateRehaPdf } from "../PatientForms/RehabForms/useCreateRehaPdf";
import { useCreateTransitionalCarePdf } from "../PatientForms/useCreateTransitionalCarePdf";
import { getRehabFormName, isRehabForm } from "./utils";

export function useFetchFormDataAndGeneratePdf({
  externalId,
}: {
  externalId: string;
}) {
  const { auctionId, patientId } = useIntParams<{
    auctionId: number;
    patientId: number;
  }>();

  const getRehabForms = useLazyRehabForms();
  const getTransitionalCare = useLazyTransitionalCare();

  const createRehaPdf = useCreateRehaPdf();
  const createTransitionalCarePdf = useCreateTransitionalCarePdf();
  const translations = useTranslations();

  const fetchAndGeneratePdf = useCallback(
    async ({ file }: { file: FileForm }) => {
      let allFileForms: AllPatientForms;
      let form: RehabForm | TransitionalCare | undefined;

      switch (file.recare_file_type) {
        case RECARE_FILE_TYPE.DRV_GENERAL_FORM:
        case RECARE_FILE_TYPE.DRV_MEDICAL_FORM:
        case RECARE_FILE_TYPE.GKV_GENERAL_FORM:
        case RECARE_FILE_TYPE.GKV_MEDICAL_FORM: {
          const rehabForms = await getRehabForms({ auctionId, patientId });

          if (!rehabForms) {
            throw new Error("Failed to fetch rehab forms");
          }
          allFileForms = {
            ...rehabForms,
            transitional_care_form: undefined,
          };
          form =
            file.recare_file_type === RECARE_FILE_TYPE.GKV_GENERAL_FORM ||
            file.recare_file_type === RECARE_FILE_TYPE.DRV_GENERAL_FORM
              ? allFileForms.general_form
              : allFileForms.medical_form;
          break;
        }
        case RECARE_FILE_TYPE.TRANSITIONAL_CARE: {
          const transitionalCare = await getTransitionalCare({
            auctionId,
            patientId,
          });
          if (!transitionalCare) {
            throw new Error("Failed to fetch transitional care");
          }
          allFileForms = {
            general_form: undefined,
            medical_form: undefined,
            transitional_care_form: transitionalCare,
          };
          form = allFileForms.transitional_care_form;
          break;
        }
        default:
          throw new Error(`Unknown recare_file_type: ${file.recare_file_type}`);
      }

      if (!form) {
        throw new Error("Form not found for the specified recare_file_type");
      }

      if (!isRehabForm(form)) {
        const pdfBytes = await createTransitionalCarePdf({
          transitionalCare: form,
        });
        return {
          fileName: `${translations.patientForms.transitionalCareForm.title}.pdf`,
          pdfBytes,
        };
      }

      const { formName, formTitle } = getRehabFormName({ form, translations });

      if (!form.form_data) {
        throw new Error("No form data available for PDF generation");
      }

      const stringified = convertEncryptedFieldIn(form.form_data as AnyObject);
      const formData = JSON.parse(stringified ?? "{}");

      const pdfBytes = await createRehaPdf({
        documentNumber: -1,
        externalId,
        formData,
        formName,
        forms: allFileForms,
      });

      return { fileName: `${formTitle}.pdf`, pdfBytes };
    },
    [
      auctionId,
      externalId,
      getRehabForms,
      getTransitionalCare,
      patientId,
      createRehaPdf,
      createTransitionalCarePdf,
    ],
  );

  return fetchAndGeneratePdf;
}
